import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";
import Experience from "./Experience";
import Portfolio from "./PortfolioContent";
import Skills from "./Skills";
import Contact from "./Contact";
import ProjectVenn from "./projects/ProjectVenn";
import ProjectJet from "./projects/ProjectJet";
import ProjectSupply from "./projects/ProjectSupply";
import ProjectYumso from "./projects/ProjectYumso";
import ProjectD3 from "./projects/ProjectD3";
import ProjectHci from "./projects/ProjectHci";
import ProjectSe from "./projects/ProjectSe";
import ProjectFitCat from "./projects/ProjectFitCat";
import ProjectGiveNext from "./projects/ProjectGiveNext";
import ProjectCscw from "./projects/ProjectCscw";
import ProjectDataProduct from "./projects/ProjectDataProduct";
import ProjectNonprofit from "./projects/ProjectNonprofit";
import ProjectRapid from "./projects/ProjectRapid";
import ProjectAiAssistant from "./projects/ProjectAiAssistant";
import ProjectDag from "./projects/ProjectDag";
import ChatWidget from "./ChatWidget";
import { VoiceCallProvider } from "../contexts/VoiceCallContext";
import VoiceCallHUD from "./VoiceCallHUD";
import { ProjectNav } from "./projects/styles";
import { PROJECT_SLUGS, getProjectNeighbors } from "../data/projects";

// Preloader animations
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const slideLeft = keyframes`
  0% { transform: translate3d(0, 0, 0); }
  100% { transform: translate3d(-100%, 0, 0); }
`;

const slideRight = keyframes`
  0% { transform: translate3d(0, 0, 0); }
  100% { transform: translate3d(100%, 0, 0); }
`;

// Preloader styles
const Preloader = styled.div<{ isLoading: boolean }>`
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  z-index: 99999;
  background: #444;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(props) => (props.isLoading ? 1 : 0)};
  visibility: ${(props) => (props.isLoading ? "visible" : "hidden")};
  transition: opacity 0.5s ease;
`;

const Spinner = styled.div`
  color: #fff;
  font-size: 16px;
  text-align: center;

  i {
    animation: ${spin} 1s linear infinite;
    margin-left: 10px;
  }
`;

const PreloaderLeft = styled.div<{ slideAway: boolean }>`
  position: fixed;
  width: 50%;
  height: 100%;
  background: white;
  z-index: 999;
  left: 0;
  top: 0;
  animation: ${(props) => (props.slideAway ? slideLeft : "none")} 0.7s 0.3s
    ease-in-out forwards;
`;

const PreloaderRight = styled.div<{ slideAway: boolean }>`
  position: fixed;
  width: 50%;
  right: 0;
  top: 0;
  height: 100%;
  background: white;
  z-index: 999;
  animation: ${(props) => (props.slideAway ? slideRight : "none")} 0.7s 0.3s
    ease-in-out forwards;
`;

// Inline menu
const InlineMenuContainer = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 30%;
  width: 70%;
  height: 40px;
  background: #f1f3f6;
  z-index: 99999;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  visibility: ${(props) => (props.isVisible ? "visible" : "hidden")};
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    width: 100%;
    left: 0;
    top: 0;
  }
`;

const InlineMenu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  float: right;
  display: flex;
  font-size: 16px;
  padding-top: 8px;

  li {
    display: inline-block;
    cursor: pointer;
    font-weight: 700;
    padding: 0 13px;
    color: #333;
    border-right: 1px solid #ddd;
    transition: all 0.3s;

    &:hover {
      color: #f34452;
    }

    &.active {
      color: #f34452;
      cursor: text;
    }

    &#close {
      font-size: 20px;
      margin-top: -4px;
      font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
      border-right: none;
      padding-left: 20px;

      &:hover {
        color: #f34452;
      }
    }
  }
`;

const HomeContainer = styled.section`
  width: 100%;
  height: 100vh;
  position: absolute;
  overflow: hidden;
  overflow-y: scroll;
  background: #444;
  background-size: cover;
  font-family: "Raleway", sans-serif;

  @media (max-width: 768px) {
    background: #333;
    overflow-y: scroll;
  }

  @media (max-width: 568px) {
    overflow-y: scroll;
  }

  @media (min-width: 569px) and (max-width: 979px) {
    height: 1150px;
    overflow-y: hidden;
  }

  @media (min-width: 980px) and (max-width: 1919px) {
    height: 850px;
    overflow-y: hidden;
  }

  @media (max-width: 370px) {
    min-width: 370px;
    overflow-x: scroll;
  }
`;

const Container = styled.div`
  position: relative;
  height: 100%;
`;

const NameBlock = styled.div<{ isReversed: boolean }>`
  position: absolute;
  width: ${(props) => (props.isReversed ? "30%" : "700px")};
  height: ${(props) => (props.isReversed ? "100%" : "700px")};
  border: ${(props) =>
    props.isReversed ? "none" : "1px solid rgba(255,255,255,0.4)"};
  display: table;
  transition: all 0.5s;
  transform: ${(props) =>
    props.isReversed ? "rotate(0deg)" : "rotate(-34deg)"};
  left: 122px;
  top: 90px;

  ${(props) =>
    props.isReversed &&
    `
    left: 0;
    top: 0;
    margin-left: 0;
    margin-top: 0;
  `}

  @media (max-width: 768px) {
    position: relative;
    transform: rotate(0deg) !important;
    width: 100% !important;
    height: auto !important;
    border: none !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.5);
    left: 0 !important;
    top: 0 !important;
    margin: 0 !important;
  }

  @media (min-width: 768px) and (max-width: 1024px) {
    transform: ${(props) =>
      props.isReversed ? "rotate(0deg)" : "rotate(-34deg)"};
    left: ${(props) => (props.isReversed ? "0" : "-190px")};
    top: ${(props) => (props.isReversed ? "0" : "10px")};
    margin-left: ${(props) => (props.isReversed ? "0" : "0")};
    margin-top: ${(props) => (props.isReversed ? "0" : "0")};
  }

  @media (min-width: 1025px) and (max-width: 1919px) {
    transform: ${(props) =>
      props.isReversed ? "rotate(0deg)" : "rotate(-34deg)"};
    left: ${(props) => (props.isReversed ? "0" : "-190px")};
    top: ${(props) => (props.isReversed ? "0" : "10px")};
    margin-left: ${(props) => (props.isReversed ? "0" : "0")};
    margin-top: ${(props) => (props.isReversed ? "0" : "0")};
  }
`;

const NameBlockContainer = styled.div<{ isReversed: boolean }>`
  display: table-cell;
  vertical-align: middle;
  padding-left: ${(props) => (props.isReversed ? "30px" : "240px")};
  padding-right: ${(props) => (props.isReversed ? "30px" : "0")};
  transform: ${(props) =>
    props.isReversed ? "rotate(0deg)" : "rotate(34deg)"};

  @media (max-width: 768px) {
    transform: rotate(0deg) !important;
    padding: 0 !important;
    text-align: center;
  }

  @media (min-width: 768px) and (max-width: 1050px) {
    h1 {
      font-size: 32px !important;
    }
    h2 {
      font-size: 20px !important;
    }
  }
`;

const Title = styled.h1`
  font-size: 50px;
  line-height: 60px;
  margin: 0;
  text-transform: uppercase;
  font-weight: 700;
  color: #fff;
  margin-bottom: 10px;
  margin-top: 50px;
  span {
    display: block;
    font-size: 30px;
    margin-bottom: 10px;
  }
`;

const Subtitle = styled.h2`
  font-size: 22px;
  line-height: 26px;
  margin: 0 0 10px 0;
  text-transform: uppercase;
  font-weight: 700;
  color: #fff;
`;

const DownloadButton = styled.a`
  background: #f34452;
  color: #fff;
  padding: 15px 30px;
  text-decoration: none;
  text-transform: uppercase;
  font-weight: 700;
  margin: 20px 0;
  display: inline-block;
  transition: all 0.3s;

  &:hover {
    background: #d63946;
    color: #fff;
    text-decoration: none;
  }
  i {
    padding-left: 10px;
  }
`;

const SocialList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 20px 0;

  li {
    display: inline-block;
    margin-right: 15px;
  }

  a {
    color: #fff;
    font-size: 20px;
    transition: all 0.3s;

    &:hover {
      color: #f34452;
    }
  }
`;

const MenuBlocks = styled.div<{ isHidden: boolean }>`
  opacity: ${(props) => (props.isHidden ? 0 : 1)};
  visibility: ${(props) => (props.isHidden ? "hidden" : "visible")};
  transition: all 0.5s;

  @media (max-width: 768px) {
    position: relative;
    width: 100%;
  }
`;

const MenuBlock = styled.div<{
  color: string;
  position: { left: string; top: string; height?: string; width?: string };
}>`
  position: absolute;
  left: ${(props) => props.position.left};
  top: ${(props) => props.position.top};
  width: ${(props) => props.position.width || "430px"};
  height: ${(props) => props.position.height || "400px"};
  background: ${(props) => props.color};
  border: 1px solid rgba(255, 255, 255, 0.4);
  display: table;
  cursor: pointer;
  transition: all 0.3s;
  transform: rotate(-34deg);

  &:hover {
    border-color: #f34452;
  }

  /* Desktop */
  @media (min-width: 1401px) and (max-width: 1919px) {
    left: ${(props) => {
      const positions = {
        "702px": "382px",
        "897px": "577px",
        "1120px": "800px",
        "1388px": "364px",
      };
      return (
        positions[props.position.left as keyof typeof positions] ||
        props.position.left
      );
    }};
    top: ${(props) => {
      const positions = {
        "-60px": "-150px",
        "179px": "89px",
        "510px": "420px",
        "136px": "540px",
      };
      return (
        positions[props.position.top as keyof typeof positions] ||
        props.position.top
      );
    }};
  }

  @media (min-width: 980px) and (max-width: 1400px) {
    left: ${(props) => {
      const positions = {
        "702px": "382px",
        "897px": "577px",
        "1120px": "800px",
        "1388px": "364px",
      };
      return (
        positions[props.position.left as keyof typeof positions] ||
        props.position.left
      );
    }};
    top: ${(props) => {
      const positions = {
        "-60px": "-150px",
        "179px": "89px",
        "510px": "420px",
        "136px": "540px",
      };
      return (
        positions[props.position.top as keyof typeof positions] ||
        props.position.top
      );
    }};
  }

  /* Tablet */
  @media (min-width: 768px) and (max-width: 980px) {
    left: ${(props) => {
      const positions = {
        "702px": "375px",
        "897px": "543px",
        "1120px": "410px",
        "1388px": "120px",
      };
      return (
        positions[props.position.left as keyof typeof positions] ||
        props.position.left
      );
    }};
    top: ${(props) => {
      const positions = {
        "-60px": "-150px",
        "179px": "98px",
        "510px": "525px",
        "136px": "721px",
      };
      return (
        positions[props.position.top as keyof typeof positions] ||
        props.position.top
      );
    }};
    width: 350px;
    height: 300px;
  }

  /* Mobile */
  @media (max-width: 768px) {
    position: relative;
    transform: rotate(0deg) !important;
    width: 100% !important;
    height: auto !important;
    border: none !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.5);
    left: 0 !important;
    top: 0 !important;
    margin: 0 !important;
    padding-left: 50px;
    &:hover {
      transform: rotate(0deg) !important;
    }
  }
`;

const MenuBlockContainer = styled.div`
  display: table-cell;
  vertical-align: middle;
  transform: rotate(34deg);
  padding: 150px 0 0 70px;

  @media (max-width: 768px) {
    transform: rotate(0deg) !important;
    padding: 0;
  }
`;

const MenuTitle = styled.h2`
  color: #fff;
  font-size: 28px;
  margin: 0;
  text-transform: uppercase;
  font-weight: 700;
  position: relative;

  &:before {
    position: absolute;
    top: -40px;
    left: -20px;
    font-size: 14px;
    color: #fff;
  }

  &:after {
    position: absolute;
    content: "";
    top: -10px;
    left: -20px;
    width: 70px;
    height: 1px;
    background: #fff;
    transition: all 0.5s ease;
  }

  &:hover:after {
    background: #f34452;
  }

  @media (max-width: 768px) {
    line-height: 3em;
    margin-bottom: 0;
    display: block;

    &:after {
      position: absolute;
      content: "";
      top: 15px;
      left: -20px;
      width: 70px;
      height: 1px;
      background: #fff;
    }
  }
`;

const ExperienceTitle = styled(MenuTitle)`
  &:before {
    content: "01";
  }
`;

const PortfolioTitle = styled(MenuTitle)`
  &:before {
    content: "02";
  }
`;

const SkillsTitle = styled(MenuTitle)`
  &:before {
    content: "03";
  }
`;

const ContactTitle = styled(MenuTitle)`
  &:before {
    content: "04";
  }
`;

const ContentBlocks = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 40px;
  left: 30%;
  right: 0;
  bottom: 0;
  background: #fff;
  z-index: 100;
  overflow-y: auto;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  visibility: ${(props) => (props.isVisible ? "visible" : "hidden")};
  transition: all 0.3s ease;
  transform: ${(props) =>
    props.isVisible ? "translateX(0)" : "translateX(100%)"};

  @media (max-width: 768px) {
    left: 0;
    right: 0;
    width: 100%;
    top: 40px;
  }
`;

const VALID_SECTIONS = ["experience", "portfolio", "skills", "contact"];
const VALID_PROJECT_SLUGS = PROJECT_SLUGS;

const PortfolioApp: React.FC = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { section, slug } = useParams<{ section?: string; slug?: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [slideAway, setSlideAway] = useState(false);
  // Lifted here so it persists when drilling into a project and back, and so
  // prev/next navigation can scope to the active filter ("*" = all projects).
  const [activeFilter, setActiveFilter] = useState("*");

  // Navigation state is derived from the URL (see App.tsx routes).
  const rawSection = slug ? "portfolio" : (section ?? null);
  const activeContent = VALID_SECTIONS.includes(rawSection ?? "")
    ? rawSection
    : null;
  const selectedProject =
    slug && VALID_PROJECT_SLUGS.includes(slug) ? slug : null;

  // The layout/animation flags all toggle together with an active section.
  const isReversed = activeContent !== null;
  const showInlineMenu = activeContent !== null;
  const hideMenuBlocks = activeContent !== null;

  // Redirect unknown sections / project slugs back home.
  useEffect(() => {
    const badSection = section != null && !VALID_SECTIONS.includes(section);
    const badSlug = slug != null && !VALID_PROJECT_SLUGS.includes(slug);
    if (badSection || badSlug) {
      navigate("/", { replace: true });
    }
  }, [section, slug, navigate]);

  // Reset scroll to the top when switching between projects.
  useEffect(() => {
    if (selectedProject) window.scrollTo(0, 0);
  }, [selectedProject]);

  // Keep the browser tab title in sync with the active section/project
  // so each URL has a distinct, descriptive title for users and search engines.
  useEffect(() => {
    const SITE = "Yuanyuan Li | Maggie";
    let pageName: string | null = null;
    if (selectedProject) {
      pageName = t(`project.${selectedProject}.title`);
    } else if (activeContent) {
      pageName = t(`nav.${activeContent}`);
    }
    document.title = pageName ? `${pageName} | ${SITE}` : SITE;
  }, [activeContent, selectedProject, t]);

  useEffect(() => {
    const handleLoad = () => {
      // Wait a minimum of 1 second for the loading experience
      const minLoadTime = 1000;
      const startTime = Date.now();

      const finishLoading = () => {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadTime - elapsedTime);

        setTimeout(() => {
          setSlideAway(true);
          setTimeout(() => {
            setIsLoading(false);
          }, 700); // Time for slide animation
        }, remainingTime);
      };

      if (document.readyState === "complete") {
        finishLoading();
      } else {
        window.addEventListener("load", finishLoading);
        return () => window.removeEventListener("load", finishLoading);
      }
    };

    handleLoad();
  }, []);

  const handleMenuClick = (content: string) => {
    navigate(`/${content}`);
  };

  const handleClose = () => {
    navigate("/");
  };

  const handleInlineMenuClick = (content: string) => {
    navigate(`/${content}`);
  };

  const handleProjectSelect = (projectComponent: string) => {
    navigate(`/portfolio/${projectComponent}`);
  };

  const handleProjectClose = () => {
    navigate("/portfolio");
  };

  const renderProjectComponent = (projectComponent: string) => {
    const projectComponents = {
      venn: <ProjectVenn />,
      jet: <ProjectJet />,
      supply: <ProjectSupply />,
      yumso: <ProjectYumso />,
      d3: <ProjectD3 />,
      hci: <ProjectHci />,
      se: <ProjectSe />,
      fitcat: <ProjectFitCat />,
      givenext: <ProjectGiveNext />,
      cscw: <ProjectCscw />,
      dataproduct: <ProjectDataProduct />,
      nonprofit: <ProjectNonprofit />,
      rapid: <ProjectRapid />,
      aiassistant: <ProjectAiAssistant />,
      dag: <ProjectDag />,
    };
    return (
      projectComponents[projectComponent as keyof typeof projectComponents] ||
      null
    );
  };

  const renderProjectView = (projectComponent: string) => {
    const { prev, next } = getProjectNeighbors(projectComponent, activeFilter);
    return (
      <>
        <ProjectNav aria-label={t("aria.projectNav")}>
          {prev ? (
            <button
              type="button"
              className="nav-link prev"
              aria-label={`${t("project.nav.prev")}: ${t(`project.${prev}.title`)}`}
              onClick={() => navigate(`/portfolio/${prev}`)}
            >
              <i className="fa fa-chevron-left"></i>
              <span>
                <span className="label">{t("project.nav.prev")}</span>
                <span className="title">{t(`project.${prev}.title`)}</span>
              </span>
            </button>
          ) : (
            <div className="spacer" />
          )}

          <button type="button" className="back" onClick={handleProjectClose}>
            <i className="fa fa-th-large"></i>{" "}
            <span className="back-label">{t("project.nav.back")}</span>
          </button>

          {next ? (
            <button
              type="button"
              className="nav-link next"
              aria-label={`${t("project.nav.next")}: ${t(`project.${next}.title`)}`}
              onClick={() => navigate(`/portfolio/${next}`)}
            >
              <i className="fa fa-chevron-right"></i>
              <span>
                <span className="label">{t("project.nav.next")}</span>
                <span className="title">{t(`project.${next}.title`)}</span>
              </span>
            </button>
          ) : (
            <div className="spacer" />
          )}
        </ProjectNav>
        {renderProjectComponent(projectComponent)}
      </>
    );
  };

  return (
    <VoiceCallProvider>
      <>
        <a
          href="#main-content"
          className="skip-link"
          style={{
            position: "absolute",
            left: "-9999px",
            zIndex: 999999,
            padding: "8px 16px",
            background: "#000",
            color: "#fff",
            textDecoration: "none",
          }}
        >
          {t("nav.skipToMain")}
        </a>

        <Preloader isLoading={isLoading}>
          <Spinner>
            <strong>
              {t("home.loading")}
              <i className="fa fa-spinner fa-spin"></i>
            </strong>
          </Spinner>
        </Preloader>
        <PreloaderLeft slideAway={slideAway} />
        <PreloaderRight slideAway={slideAway} />

        <InlineMenuContainer
          isVisible={showInlineMenu && !isLoading}
          role="navigation"
          aria-label={t("aria.secondaryNavigation")}
        >
          <InlineMenu role="menubar" aria-label={t("aria.portfolioSections")}>
            <li
              className={`about menu-item ${
                activeContent === "experience" ? "active" : ""
              }`}
              onClick={() => handleInlineMenuClick("experience")}
              role="menuitem"
              tabIndex={0}
              aria-label={t("aria.navigateToExperience")}
              onKeyDown={(e) =>
                e.key === "Enter" && handleInlineMenuClick("experience")
              }
            >
              {t("nav.experience")}
            </li>
            <li
              className={`portfolio menu-item ${
                activeContent === "portfolio" ? "active" : ""
              }`}
              onClick={() => handleInlineMenuClick("portfolio")}
              role="menuitem"
              tabIndex={0}
              aria-label={t("aria.navigateToPortfolio")}
              onKeyDown={(e) =>
                e.key === "Enter" && handleInlineMenuClick("portfolio")
              }
            >
              {t("nav.portfolio")}
            </li>
            <li
              className={`blog menu-item ${
                activeContent === "skills" ? "active" : ""
              }`}
              onClick={() => handleInlineMenuClick("skills")}
              role="menuitem"
              tabIndex={0}
              aria-label={t("aria.navigateToSkills")}
              onKeyDown={(e) =>
                e.key === "Enter" && handleInlineMenuClick("skills")
              }
            >
              {t("nav.skills")}
            </li>
            <li
              className={`contact menu-item ${
                activeContent === "contact" ? "active" : ""
              }`}
              onClick={() => handleInlineMenuClick("contact")}
              role="menuitem"
              tabIndex={0}
              aria-label={t("aria.navigateToContact")}
              onKeyDown={(e) =>
                e.key === "Enter" && handleInlineMenuClick("contact")
              }
            >
              {t("nav.contact")}
            </li>
            <li
              id="close"
              onClick={handleClose}
              role="button"
              tabIndex={0}
              aria-label={t("aria.closeNavigation")}
              onKeyDown={(e) => e.key === "Enter" && handleClose()}
            >
              &times;
            </li>
          </InlineMenu>
        </InlineMenuContainer>
        <main
          id="main-content"
          role="main"
          aria-label={t("aria.portfolioWebsite")}
        >
          <HomeContainer role="region" aria-label={t("aria.portfolioHomepage")}>
            <Container>
              <NameBlock isReversed={isReversed} as="header">
                <NameBlockContainer isReversed={isReversed}>
                  <Title as="h1" aria-label={t("aria.nameTitle")}>
                    <span>{t("home.greeting")}</span>
                    {t("home.name")}
                  </Title>
                  <Subtitle as="h2">{t("home.title1")}</Subtitle>
                  <Subtitle as="h2">{t("home.title2")}</Subtitle>
                  <Subtitle as="h2">{t("home.title3")}</Subtitle>
                  <DownloadButton
                    href={
                      language === "zh"
                        ? "/documents/YuanyuanLi-Resume-zh.pdf"
                        : "/documents/YuanyuanLi-Resume.pdf"
                    }
                    download
                    aria-label={t("aria.downloadResume")}
                  >
                    {t("home.downloadResume")}
                    <i className="fa fa-download"></i>
                  </DownloadButton>
                  <SocialList
                    role="navigation"
                    aria-label={t("aria.socialLinks")}
                  >
                    <li>
                      <a
                        href="https://www.linkedin.com/in/yuanyuanmaggie"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t("aria.linkedinProfile")}
                      >
                        <i className="fa fa-linkedin"></i>
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://github.com/YuanyuanMaggie"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t("aria.githubProfile")}
                      >
                        <i className="fa fa-github"></i>
                      </a>
                    </li>
                  </SocialList>
                </NameBlockContainer>
              </NameBlock>

              <MenuBlocks
                isHidden={hideMenuBlocks}
                as="nav"
                role="navigation"
                aria-label={t("aria.mainNavigation")}
              >
                <MenuBlock
                  color="transparent"
                  position={{
                    left: "702px",
                    top: "-60px",
                    height: "300px",
                  }}
                  onClick={() => handleMenuClick("experience")}
                  role="button"
                  tabIndex={0}
                  aria-label={t("aria.viewExperience")}
                  onKeyDown={(e) =>
                    e.key === "Enter" && handleMenuClick("experience")
                  }
                >
                  <MenuBlockContainer>
                    <ExperienceTitle>{t("nav.experience")}</ExperienceTitle>
                  </MenuBlockContainer>
                </MenuBlock>
                <MenuBlock
                  color="transparent"
                  position={{
                    left: "897px",
                    top: "179px",
                  }}
                  onClick={() => handleMenuClick("portfolio")}
                  role="button"
                  tabIndex={0}
                  aria-label={t("aria.viewPortfolio")}
                  onKeyDown={(e) =>
                    e.key === "Enter" && handleMenuClick("portfolio")
                  }
                >
                  <MenuBlockContainer>
                    <PortfolioTitle>{t("nav.portfolio")}</PortfolioTitle>
                  </MenuBlockContainer>
                </MenuBlock>
                <MenuBlock
                  color="transparent"
                  position={{
                    left: "1120px",
                    top: "510px",
                  }}
                  onClick={() => handleMenuClick("skills")}
                  role="button"
                  tabIndex={0}
                  aria-label={t("aria.viewSkills")}
                  onKeyDown={(e) =>
                    e.key === "Enter" && handleMenuClick("skills")
                  }
                >
                  <MenuBlockContainer>
                    <SkillsTitle>{t("nav.skills")}</SkillsTitle>
                  </MenuBlockContainer>
                </MenuBlock>
                <MenuBlock
                  color="transparent"
                  position={{
                    left: "1388px",
                    top: "136px",
                  }}
                  onClick={() => handleMenuClick("contact")}
                  role="button"
                  tabIndex={0}
                  aria-label={t("aria.viewContact")}
                  onKeyDown={(e) =>
                    e.key === "Enter" && handleMenuClick("contact")
                  }
                >
                  <MenuBlockContainer>
                    <ContactTitle>{t("nav.contact")}</ContactTitle>
                  </MenuBlockContainer>
                </MenuBlock>
              </MenuBlocks>

              <ContentBlocks
                isVisible={activeContent === "experience"}
                role="region"
                aria-label={t("aria.experienceContent")}
                aria-hidden={activeContent !== "experience"}
              >
                <Experience />
              </ContentBlocks>
              <ContentBlocks
                isVisible={activeContent === "portfolio"}
                role="region"
                aria-label={t("aria.portfolioContent")}
                aria-hidden={activeContent !== "portfolio"}
              >
                {selectedProject ? (
                  renderProjectView(selectedProject)
                ) : (
                  <Portfolio
                  onProjectSelect={handleProjectSelect}
                  activeFilter={activeFilter}
                  onFilterChange={setActiveFilter}
                />
                )}
              </ContentBlocks>
              <ContentBlocks
                isVisible={activeContent === "skills"}
                role="region"
                aria-label={t("aria.skillsContent")}
                aria-hidden={activeContent !== "skills"}
              >
                <Skills />
              </ContentBlocks>
              <ContentBlocks
                isVisible={activeContent === "contact"}
                role="region"
                aria-label={t("aria.contactContent")}
                aria-hidden={activeContent !== "contact"}
              >
                <Contact />
              </ContentBlocks>
            </Container>
          </HomeContainer>
          <LanguageSwitcher />
          <VoiceCallHUD />
          <ChatWidget isDarkMode={true} />
        </main>
      </>
    </VoiceCallProvider>
  );
};

export default PortfolioApp;
